import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatCurrency } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Users, Car, Radio, Wallet, ShieldCheck, TrendingUp, Route as RouteIcon } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";

const KpiCard = ({ icon: Icon, label, value, sub, tone = "mint", testid, onClick }) => {
    const toneClass = {
        mint: "bg-mint-50 text-mint-700",
        navy: "bg-slate-100 text-navy-700",
        sky: "bg-sky-50 text-sky-700",
        amber: "bg-amber-50 text-amber-700",
    }[tone];
    return (
        <button
            type="button"
            onClick={onClick}
            className="bg-white border border-slate-200 rounded-2xl p-5 text-left w-full hover:border-mint-300 hover:shadow-sm transition-all"
            data-testid={testid}
        >
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</div>
                    <div className="font-display font-bold text-navy-900 text-2xl mt-2">{value}</div>
                    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
                </div>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${toneClass}`}>
                    <Icon className="w-5 h-5" />
                </div>
            </div>
        </button>
    );
};

const Dashboard = () => {
    const [stats, setStats] = useState(null);
    const [trend, setTrend] = useState([]);
    const [split, setSplit] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        Promise.all([
            api.get("/dashboard/stats"),
            api.get("/dashboard/revenue-trend?days=14"),
            api.get("/dashboard/vehicle-split"),
        ]).then(([s, t, v]) => {
            setStats(s.data);
            setTrend(t.data);
            setSplit(v.data);
        });
    }, []);

    if (!stats) return <div className="text-slate-500 text-sm">Loading dashboard…</div>;

    const trendData = trend.map((d) => ({
        date: new Date(d.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
        revenue: d.revenue,
        commission: d.commission,
        rides: d.rides,
    }));

    return (
        <div className="space-y-6">
            <PageHeader title="Dashboard" subtitle="Live operations overview" />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard icon={Radio} label="Live Rides" value={stats.live_rides} sub={`${stats.active_drivers} drivers online`} testid="kpi-live-rides" onClick={() => navigate("/live")} />
                <KpiCard icon={Wallet} label="Today's Revenue" value={formatCurrency(stats.today_revenue)} sub={`${stats.today_rides} rides today`} tone="navy" testid="kpi-today-revenue" onClick={() => navigate("/earnings")} />
                <KpiCard icon={Users} label="Customers" value={stats.total_customers} sub="lifetime" tone="sky" testid="kpi-customers" onClick={() => navigate("/customers")} />
                <KpiCard icon={Car} label="Drivers" value={stats.total_drivers} sub={`${stats.pending_kyc} pending KYC`} tone="amber" testid="kpi-drivers" onClick={() => navigate("/drivers")} />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard icon={RouteIcon} label="Total Rides" value={stats.total_rides} sub={`${stats.completed_rides} completed`} testid="kpi-total-rides" onClick={() => navigate("/rides")} />
                <KpiCard icon={TrendingUp} label="Total Revenue" value={formatCurrency(stats.total_revenue)} sub="all-time" tone="navy" testid="kpi-total-revenue" onClick={() => navigate("/earnings")} />
                <KpiCard icon={Wallet} label="Commission" value={formatCurrency(stats.total_commission)} sub="per-rule %" tone="sky" testid="kpi-commission" onClick={() => navigate("/earnings")} />
                <KpiCard icon={ShieldCheck} label="Pending KYC" value={stats.pending_kyc} sub="drivers waiting" tone="amber" testid="kpi-pending-kyc" onClick={() => navigate("/drivers?kyc=pending")} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-5">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="font-display font-semibold text-navy-900">Revenue trend</div>
                            <div className="text-xs text-slate-500">Last 14 days</div>
                        </div>
                        <div className="flex gap-3 text-xs">
                            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-mint-500" /> Revenue</span>
                            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-sky-500" /> Commission</span>
                        </div>
                    </div>
                    <div style={{ width: "100%", height: 280 }}>
                        <ResponsiveContainer>
                            <AreaChart data={trendData}>
                                <defs>
                                    <linearGradient id="gMint" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#26C281" stopOpacity={0.4} />
                                        <stop offset="95%" stopColor="#26C281" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="gSky" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#0EA5E9" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#0EA5E9" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                                <Area type="monotone" dataKey="revenue" stroke="#26C281" strokeWidth={2.5} fill="url(#gMint)" />
                                <Area type="monotone" dataKey="commission" stroke="#0EA5E9" strokeWidth={2.5} fill="url(#gSky)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl p-5">
                    <div className="font-display font-semibold text-navy-900 mb-4">Rides by vehicle type</div>
                    <div style={{ width: "100%", height: 280 }}>
                        <ResponsiveContainer>
                            <BarChart data={split} layout="vertical" margin={{ left: 8 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                                <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                                <YAxis dataKey="vehicle_type" type="category" tick={{ fontSize: 11, fill: "#0F1F2D" }} width={92} axisLine={false} tickLine={false} />
                                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                                <Bar dataKey="count" fill="#26C281" radius={[0, 6, 6, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
